import { SensorType } from '@frontend/device/types';
import { MqttBroker } from '@frontend/pub-sub';
import { useEffect, useState } from 'react';

import { useAppSelector } from '../../redux/store';

interface UseScannersProps {
    id: string;
    onDeviceSensorReceived?: (input: string, data: any) => void;
    onQrCodeScanned?: (code: string) => void;
    onKeyboardInput?: (input: string) => void;
    onTagScanned?: (code: string) => void;
}

interface UseScannersReturn {
    qrCode?: string;
    resetQrCode?: () => void;
}

/**
 * Hook to manage events sent directly from scanners.
 * Can be used to give direct feedback to the user when a code is scanned, without having to wait for the backend to process the code and return a processed response.
 *
 * @todo implement NFC codes
 */
export const useScanners = (props: UseScannersProps): UseScannersReturn => {
    const navigationState = useAppSelector((store) => store.navigation);
    const IotId = navigationState.iot?.id;

    const [qrCode, setQrCode] = useState<string | undefined>(undefined);
    const onQrCodeScanned = (data: any) => {
        if (!data.data.qr_code) {
            console.warn('Received QR code scan event without qr_code data', data);
            return;
        }
        setQrCode(data.data.qr_code);
        if (props.onQrCodeScanned) props.onQrCodeScanned(data.data.qr_code);
        if (props.onDeviceSensorReceived) props.onDeviceSensorReceived(data.data.qr_code, data);
    };
    const onKeyboardInput = (data: any) => {
        if (!data.data.input) {
            console.warn('Received keyboard input event without input data', data);
            return;
        }
        if (props.onKeyboardInput) props.onKeyboardInput(data.data.input);
        if (props.onDeviceSensorReceived) props.onDeviceSensorReceived(data.data.input, data);
    };
    const onTagScanned = (data: any) => {
        if (!data.data.tag_code) {
            console.warn('Received tag scan event without tag data', data);
            return;
        }
        if (props.onTagScanned) props.onTagScanned(data.data.tag_code);
        if (props.onDeviceSensorReceived) props.onDeviceSensorReceived(data.data.tag_code, data);
    };

    const callback = (message: string, data: any) => {
        if (message === 'device_sensor_received' && data?.type === SensorType.QR_CODE) onQrCodeScanned(data);
        if (message === 'device_sensor_received' && data?.type === SensorType.TEXT) onKeyboardInput(data);
        if (message === 'device_sensor_received' && data?.type === SensorType.TAG_CODE) onTagScanned(data);
    };

    useEffect(() => {
        if (IotId) {
            MqttBroker.getInstance().subscribe(props.id + '_USE_SCANNERS', 'iots/' + IotId + '/sensor', callback);
        }

        return () => {
            if (IotId) {
                MqttBroker.getInstance().unSubscribe(props.id + '_USE_SCANNERS', 'iots/' + IotId + '/sensor');
            }
        };
    }, [props.id, IotId]);

    return {
        qrCode,
        resetQrCode: () => setQrCode(undefined)
    };
};
